import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import {
  getCountryCallingCode,
  parsePhoneNumber,
  type Country,
} from "react-phone-number-input/max"

import {
  Select as CovalSelect,
  SelectItem as CovalSelectItem,
  Input,
  SelectContent,
  SelectTrigger,
} from "@coval/ui"
import * as Flags from "country-flag-icons/react/3x2"
import { AsYouType, getExampleNumber } from "libphonenumber-js/max"
import mobileExamples from "libphonenumber-js/mobile/examples"

interface PhoneNumberInputProps {
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  className?: string
  error?: string
  hidePlaceholder?: boolean
}

const COMMON_COUNTRIES: ReadonlyArray<{ code: Country; name: string }> = [
  { code: "US", name: "United States" },
  { code: "CA", name: "Canada" },
  { code: "CO", name: "Colombia" },
  { code: "GB", name: "United Kingdom" },
  { code: "AU", name: "Australia" },
  { code: "IN", name: "India" },
  { code: "DE", name: "Germany" },
  { code: "FR", name: "France" },
  { code: "ES", name: "Spain" },
  { code: "IT", name: "Italy" },
  { code: "BR", name: "Brazil" },
  { code: "MX", name: "Mexico" },
  { code: "JP", name: "Japan" },
  { code: "CN", name: "China" },
] as const

const COUNTRY_CODE_MAP: Record<string, Country> = {
  "1": "US",
  "33": "FR",
  "34": "ES",
  "39": "IT",
  "44": "GB",
  "49": "DE",
  "52": "MX",
  "55": "BR",
  "57": "CO",
  "61": "AU",
  "81": "JP",
  "86": "CN",
}

const Flag = ({ code }: { code: string }) => {
  const FlagComponent = Flags[code as keyof typeof Flags]
  if (!FlagComponent) {
    return null
  }
  return <FlagComponent className="w-5 h-auto shrink-0" />
}

const detectCountryFromValue = (phoneValue: string): Country => {
  if (!phoneValue) {
    return "US"
  }

  try {
    const parsed = parsePhoneNumber(phoneValue)
    if (parsed?.country) {
      return parsed.country
    }
  } catch {
    // Incomplete and legacy values still fall back to a recognizable prefix.
  }

  const match = phoneValue.match(/^\+?(\d{1,3})/)
  if (match) {
    const code = match[1]
    for (const [countryCode, country] of Object.entries(COUNTRY_CODE_MAP)) {
      if (code.startsWith(countryCode)) {
        return country
      }
    }
  }

  return "US"
}

const stripCountryCode = (phoneValue: string, country: Country): string => {
  if (!phoneValue) {
    return ""
  }

  const digitsOnly = phoneValue.replace(/[^\d]/g, "")
  const countryCode = getCountryCallingCode(country)

  if (digitsOnly.startsWith(countryCode)) {
    return digitsOnly.slice(countryCode.length)
  }

  return digitsOnly
}

const formatSavedNumber = (phoneValue: string, country: Country): string => {
  if (!phoneValue) {
    return ""
  }

  try {
    const parsed = parsePhoneNumber(phoneValue)
    if (parsed?.country === country) {
      return parsed.isValid()
        ? parsed.formatNational()
        : stripCountryCode(phoneValue, country)
    }
  } catch {
    // Keep malformed saved values editable instead of failing to render.
  }

  return new AsYouType(country).input(stripCountryCode(phoneValue, country))
}

const formatNationalInput = (input: string, country: Country) => {
  const digits = input.replace(/\D/g, "")
  if (!digits) {
    return { display: "", e164: "" }
  }
  const formatter = new AsYouType(country)
  const display = formatter.input(digits).replace(/\D+$/, "")
  return {
    display,
    e164:
      formatter.getNumberValue() ??
      `+${getCountryCallingCode(country)}${digits}`,
  }
}

const countDigits = (text: string): number => text.replace(/\D/g, "").length

const caretAfterDigit = (formatted: string, digitOffset: number): number => {
  if (digitOffset === 0) {
    return 0
  }
  if (digitOffset >= countDigits(formatted)) {
    return formatted.length
  }

  let seen = 0
  for (let index = 0; index < formatted.length; index += 1) {
    if (/\d/.test(formatted[index])) {
      seen += 1
      if (seen === digitOffset) {
        return index + 1
      }
    }
  }
  return formatted.length
}

export const PhoneNumberInput: React.FC<PhoneNumberInputProps> = ({
  value,
  onChange,
  disabled = false,
  error,
  className = "",
  hidePlaceholder = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const pendingDigitOffsetRef = useRef<number | null>(null)
  const [isFocused, setIsFocused] = useState(false)
  const [manuallySelectedCountry, setManuallySelectedCountry] =
    useState<Country | null>(null)
  const [draft, setDraft] = useState<{
    country: Country
    display: string
    e164: string
  } | null>(null)

  const detectedCountry = useMemo(() => detectCountryFromValue(value), [value])
  const selectedCountry = manuallySelectedCountry || detectedCountry

  const countryCode = useMemo(
    () => getCountryCallingCode(selectedCountry),
    [selectedCountry],
  )
  const exampleNumber = useMemo(
    () =>
      getExampleNumber(selectedCountry, mobileExamples)?.formatNational() ??
      "Phone number",
    [selectedCountry],
  )

  const commitNationalInput = useCallback(
    (input: string, digitOffset: number) => {
      const next = formatNationalInput(input, selectedCountry)
      pendingDigitOffsetRef.current = digitOffset
      setDraft({ country: selectedCountry, ...next })
      if (next.e164 !== value) {
        onChange(next.e164)
      }
    },
    [onChange, selectedCountry, value],
  )

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const caret = event.target.selectionStart ?? event.target.value.length
      commitNationalInput(
        event.target.value,
        countDigits(event.target.value.slice(0, caret)),
      )
    },
    [commitNationalInput],
  )

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== "Backspace" && event.key !== "Delete") {
        return
      }
      const input = event.currentTarget
      const start = input.selectionStart
      if (start === null || start !== input.selectionEnd) {
        return
      }
      const adjacent =
        event.key === "Backspace" ? input.value[start - 1] : input.value[start]
      if (!adjacent || /\d/.test(adjacent)) {
        return
      }

      const digits = input.value.replace(/\D/g, "")
      const digitsBeforeCaret = countDigits(input.value.slice(0, start))
      const digitToRemove =
        event.key === "Backspace" ? digitsBeforeCaret - 1 : digitsBeforeCaret
      event.preventDefault()
      if (digitToRemove < 0 || digitToRemove >= digits.length) {
        return
      }
      const nextDigits =
        digits.slice(0, digitToRemove) + digits.slice(digitToRemove + 1)
      commitNationalInput(nextDigits, digitToRemove)
    },
    [commitNationalInput],
  )

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      const pastedText = e.clipboardData.getData("text").trim()
      if (!pastedText) {
        return
      }

      // With '+': parse as international; may auto-switch the country.
      if (pastedText.startsWith("+")) {
        try {
          const parsed = parsePhoneNumber(pastedText)
          const pastedCountry =
            parsed?.country ??
            (parsed?.countryCallingCode === countryCode
              ? selectedCountry
              : COUNTRY_CODE_MAP[parsed?.countryCallingCode ?? ""])
          const digits = pastedText.replace(/\D/g, "")
          const country =
            pastedCountry ??
            Object.entries(COUNTRY_CODE_MAP).find(([code]) =>
              digits.startsWith(code),
            )?.[1]
          if (country) {
            e.preventDefault()
            if (country !== selectedCountry) {
              setManuallySelectedCountry(country)
            }
            setDraft(null)
            pendingDigitOffsetRef.current = countDigits(
              stripCountryCode(parsed?.number ?? pastedText, country),
            )
            onChange(
              formatNationalInput(
                stripCountryCode(parsed?.number ?? pastedText, country),
                country,
              ).e164,
            )
          }
        } catch {
          // Fall through to handleChange.
        }
      }
    },
    [onChange, selectedCountry, countryCode],
  )

  const handleCountryChange = useCallback(
    (newCountry: string) => {
      const country = newCountry as Country
      setManuallySelectedCountry(country)
      setDraft(null)
      pendingDigitOffsetRef.current = null
      const newCode = getCountryCallingCode(country)
      const nationalNumber = stripCountryCode(value, selectedCountry)
      onChange(nationalNumber ? `+${newCode}${nationalNumber}` : "")
    },
    [onChange, value, selectedCountry],
  )

  const handleFocus = useCallback(() => setIsFocused(true), [])
  const handleBlur = useCallback(() => {
    pendingDigitOffsetRef.current = null
    setIsFocused(false)
  }, [])

  const showError = value.length > 0 && !!error && !isFocused
  const displayValue =
    draft?.country === selectedCountry && draft.e164 === value
      ? draft.display
      : formatSavedNumber(value, selectedCountry)

  useLayoutEffect(() => {
    const digitOffset = pendingDigitOffsetRef.current
    const input = inputRef.current
    if (digitOffset === null || !input || document.activeElement !== input) {
      return
    }
    pendingDigitOffsetRef.current = null
    const caret = caretAfterDigit(displayValue, digitOffset)
    input.setSelectionRange(caret, caret)
  }, [displayValue])

  return (
    <div className={className}>
      <div className="coval-focus-inset inline-flex h-control w-fit max-w-full overflow-hidden rounded-md border border-surface-1 bg-surface-default">
        <div className="shrink-0 border-r border-surface-1">
          <CovalSelect
            value={selectedCountry}
            onValueChange={handleCountryChange}
            disabled={disabled}
          >
            <SelectTrigger
              aria-label="Country code"
              className="h-full w-max rounded-none border-0 focus:border-0"
            >
              <span className="flex items-center gap-1.5">
                <Flag code={selectedCountry} />
                <span>+{countryCode}</span>
              </span>
            </SelectTrigger>
            <SelectContent className="w-48">
              {COMMON_COUNTRIES.map((country) => (
                <CovalSelectItem
                  key={country.code}
                  value={country.code}
                  textValue={`+${getCountryCallingCode(country.code)}`}
                >
                  <span className="flex items-center gap-2">
                    <Flag code={country.code} />
                    <span className="flex-1">{country.name}</span>
                    <span className="text-coval-text-muted">
                      +{getCountryCallingCode(country.code)}
                    </span>
                  </span>
                </CovalSelectItem>
              ))}
            </SelectContent>
          </CovalSelect>
        </div>
        <div className="grid min-w-0 max-w-full">
          <span
            aria-hidden="true"
            className="invisible col-start-1 row-start-1 whitespace-pre px-2 pr-4 text-sm"
          >
            {exampleNumber}
          </span>
          <Input
            ref={inputRef}
            type="tel"
            inputMode="tel"
            autoComplete="tel-national"
            value={displayValue}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onFocus={handleFocus}
            onBlur={handleBlur}
            disabled={disabled}
            placeholder={
              hidePlaceholder ? "Enter a phone number" : exampleNumber
            }
            className="col-start-1 row-start-1 h-full min-w-0 rounded-none border-0 focus:border-0"
            error={showError}
            aria-label="Phone number"
          />
        </div>
      </div>
      {showError && (
        <p className="text-xs text-coval-danger-text mt-1">
          Please enter a valid phone number
        </p>
      )}
    </div>
  )
}
